
  
 import { useEffect, useState } from "react";
 import { useSelector } from "react-redux";
 import { useLocation, useNavigate } from "react-router-dom";
 import CreateNewSessionModal from "../../Resource/Popup/CreateSessionPopup";
 import { useMeritEngineNavigation } from "../../useMeritEngineNavigation";
 import LgbrTable from "../../LGBR/LgbrIndex";
 
 const DataTabs = [
   { label: "LOAD", step: 0, tab: "load-price" },
   { label: "GENERATION", step: 1, tab: "resources" },
   { label: "MOD", step: 2, tab: "merit-order-dispatch" },
   { label: "BID", step: 3, tab: "bid" },
 ];
 
 export default function ModHeader({ title }: { title: string }) {
   const tabNames = useSelector((state: any) => state.switchTabs.tabNames);
   const modSelectedSession = useSelector(
    (state: any) => state.modDemoSession.modSession
  );
   const VISITED_KEY = "TabVisited_Demo";
   const location = useLocation();
   const { navigateToPage, currentPage } = useMeritEngineNavigation();
 
   const [activeTab, setActiveTab] = useState<string | undefined>();
   const [isModalOpen, setModalOpen] = useState(false);
   const [stageCompleted, setStageCompleted] = useState<{
     [key: string]: boolean;
   }>({});
 
   // Initialize visitedTabs from sessionStorage
   const [visitedTabs, setVisitedTabs] = useState<number[]>(() => {
     try {
       const raw = sessionStorage.getItem(VISITED_KEY);
       if (raw) {
         const parsed = JSON.parse(raw);
         if (Array.isArray(parsed) && parsed.length > 0) {
           return parsed as number[];
         }
       }
     } catch (e) {
       console.error("Error parsing visited tabs:", e);
     }
     return [0];
   });
 
   // Track if session was previously set
   const [sessionWasSet, setSessionWasSet] = useState(() => {
     return modSelectedSession !== null;
   });
 
   // Listen for storage changes (including manual removal)
   useEffect(() => {
     const handleStorageCheck = () => {
       const currentStorage = sessionStorage.getItem(VISITED_KEY);
       
       // If storage is null/empty, reset to initial state
       if (!currentStorage || currentStorage === "null") {
         setVisitedTabs([0]);
         setStageCompleted({});
         setActiveTab("LOAD");
         navigateToPage("load-price");
       }
     };
 
     // Check periodically for storage changes (for same-tab detection)
     const interval = setInterval(handleStorageCheck, 500);
 
     // Also listen for storage events (for cross-tab changes)
     window.addEventListener("storage", handleStorageCheck);
 
     return () => {
       clearInterval(interval);
       window.removeEventListener("storage", handleStorageCheck);
     };
   }, [navigateToPage]);
 
   // Clear visited tabs only when session is explicitly removed
   useEffect(() => {
     if (modSelectedSession !== null) {
       setSessionWasSet(true);
     } else if (sessionWasSet && modSelectedSession === null) {
       setVisitedTabs([0]);
       sessionStorage.removeItem(VISITED_KEY);
       setStageCompleted({});
       navigateToPage("load-price");
     }
   }, [modSelectedSession, sessionWasSet, navigateToPage]);
 
   // Persist visitedTabs to sessionStorage
   useEffect(() => {
     try {
       sessionStorage.setItem(VISITED_KEY, JSON.stringify(visitedTabs));
     } catch (e) {
       console.error("Error saving visited tabs:", e);
     }
   }, [visitedTabs]);
 
   // Sync activeTab with currentPage and update visitedTabs
   useEffect(() => {
     if (!currentPage) return;
     
     const currentTab = DataTabs.find((tab) => tab.tab === currentPage);
 
     if (currentTab) {
       setActiveTab(currentTab.label);
 
       setVisitedTabs((prev) => {
         const allStepsUpToCurrent = Array.from(
           { length: currentTab.step + 1 },
           (_, i) => i
         );
 
         const mergedSteps = Array.from(
           new Set([...prev, ...allStepsUpToCurrent])
         ).sort((a, b) => a - b);
 
         if (JSON.stringify(prev) !== JSON.stringify(mergedSteps)) {
           return mergedSteps;
         }
 
         return prev;
       });
     }
   }, [currentPage]);
 
   // Mark stage as completed when tab is active
   useEffect(() => {
     if (activeTab && modSelectedSession !== null) {
       setStageCompleted((prev) => ({ ...prev, [activeTab]: true }));
     }
   }, [activeTab, modSelectedSession]);
 
   // Handle tab navigation
   const handleTabChange = (tabLabel: string) => {
     const selectedTab = DataTabs.find((tab) => tab.label === tabLabel);
     const currentTab = DataTabs.find((tab) => tab.label === activeTab);
 
     if (!selectedTab) return;
 
     const hasVisited = visitedTabs.includes(selectedTab.step);
 
     if (!currentTab) {
       navigateToPage(selectedTab.tab as any);
       setVisitedTabs((prev) => {
         const allStepsUpToSelected = Array.from(
           { length: selectedTab.step + 1 },
           (_, i) => i
         );
         const merged = Array.from(
           new Set([...prev, ...allStepsUpToSelected])
         ).sort((a, b) => a - b);
         return merged;
       });
       return;
     }
 
     if (selectedTab.step <= currentTab.step || hasVisited) {
       navigateToPage(selectedTab.tab as any);
 
       if (!hasVisited) {
         setVisitedTabs((prev) => {
           const allStepsUpToSelected = Array.from(
             { length: selectedTab.step + 1 },
             (_, i) => i
           );
           const merged = Array.from(
             new Set([...prev, ...allStepsUpToSelected])
           ).sort((a, b) => a - b);
           return merged;
         });
       }
     } else {
       console.warn(
         `Cannot navigate to ${tabLabel}. Complete previous tabs first.`
       );
     }
   };
 
   // Function to reset tabs after current tab (disable next tabs)
   const resetNextTabs = (fromTabLabel?: string) => {
     const currentTab = fromTabLabel 
       ? DataTabs.find((tab) => tab.label === fromTabLabel)
       : DataTabs.find((tab) => tab.label === activeTab);
 
     if (!currentTab) {
       console.warn("No current tab found for reset");
       return;
     }
 
     // Keep only tabs up to and including current tab
     const updatedVisitedTabs = Array.from(
       { length: currentTab.step + 1 },
       (_, i) => i
     );
     
     setVisitedTabs(updatedVisitedTabs);
     sessionStorage.setItem(VISITED_KEY, JSON.stringify(updatedVisitedTabs));
 
     // Clear completion status for tabs after current
     setStageCompleted((prev) => {
       const newCompleted = { ...prev };
       DataTabs.forEach((tab) => {
         if (tab.step > currentTab.step) {
           delete newCompleted[tab.label];
         }
       });
       return newCompleted;
     });
   };
 
   // Reset visited tabs to initial state
   const resetVisitedTabs = () => {
     setVisitedTabs([0]);
     setSessionWasSet(false);
     sessionStorage.setItem(VISITED_KEY, JSON.stringify([0]));
     setStageCompleted({});
   };
 
   // Force tab reset (exposed globally for debugging)
   const forceTabReset = () => {
     setVisitedTabs([0]);
     setSessionWasSet(false);
     sessionStorage.setItem(VISITED_KEY, JSON.stringify([0]));
     setStageCompleted({});
     setActiveTab("LOAD");
     navigateToPage("load-price");
   };
 
   // Expose functions globally
   useEffect(() => {
     (window as any).forceTabReset = forceTabReset;
     (window as any).resetNextTabs = resetNextTabs;
     return () => {
       delete (window as any).forceTabReset;
       delete (window as any).resetNextTabs;
     };
   }, [activeTab, visitedTabs]);
 
   return (
     <div className="bg-white p-[0.5rem] rounded-xl flex flex-wrap gap-3 justify-between items-center">
       <div className="flex items-center gap-4">
         <button
           onClick={() => setModalOpen(true)}
           className="flex items-center gap-1 text-sm rounded-2xl p-2 bg-[#FFFAF6] text-[#F1935C] font-medium border border-[#F1935C] hover transition transform hover:scale-105"
         >
           + <span>Create a New Session</span>
         </button>
         {(activeTab === "MOD" || activeTab === "BID") && <LgbrTable />}
       </div>
 
       <h2 className="chartHeading !text-lg !font-medium w-100">{title}</h2>
 
       {DataTabs.length === 1 ? (
         <div className="tab tab-active rounded-lg">{tabNames[0]}</div>
       ) : (
         <div className="bg-[#FAFAFA] rounded-full shadow-inner flex w-fit border-2 border-[#FAFAFA]">
           {DataTabs.map((tab) => {
             const isDisabled = !visitedTabs.includes(tab.step);
             const isActive = activeTab === tab.label;
 
             return (
               <button
                 key={tab.label}
                 onClick={() => {
                   if (!isDisabled) handleTabChange(tab.label);
                 }}
                 disabled={isDisabled}
                 className={`px-4 py-2 rounded-full transition-all duration-100 text-sm font-medium capitalize
                   ${
                     isActive
                       ? "bg-white text-[#F1935C] shadow transition active:shadow-md"
                       : isDisabled
                       ? "text-[#95949a] cursor-not-allowed opacity-50"
                       : "text-[#6F6D7C] hover:text-[#F1935C] cursor-pointer"
                   }`}
                 title={isDisabled ? "Complete previous tabs to unlock" : ""}
               >
                 {tab.label.charAt(0).toUpperCase() + tab.label.slice(1)}
               </button>
             );
           })}
         </div>
       )}
 
       <CreateNewSessionModal
         isOpen={isModalOpen}
         onClose={() => {
           setModalOpen(false);
         }}
       />
     </div>
   );
 }